import { useParams } from 'react-router-dom';
import useSWR from 'swr';

import { useShop } from '@services/ShopProvider';
import { getChannels } from '@services/channels';
import { customiseProduct, useStock } from '@services/stock';
import { useIsChain } from '@utils/useIsChain';

import { StockData, StockDataSummary } from '../StockData';
import { Card, Stack, StatusIcon, Alert } from '@nearst/ui';
import EditForm from '../../components/EditForm';
import ItemStatus from '../ItemStatusCard';
import ChannelVisibilityCard from '../ChannelVisibility';
import ProductCategory from '../ProductCategory';
import DeliveryChannelsCard from '../DeliveryChannelsCard';
import ProductSource from '../../components/ProductSource';

import styles from './ProductDetails.module.scss';

const ProductDetails = () => {
	const { barcode, shopId } = useParams();
	const { data: stockItem } = useStock(barcode, shopId, { suspense: true });
	const { data: chain, shop } = useShop();

	const shops = shop && shop.id ? [shop] : chain;
	const { data: channels } = useSWR(`channels-${shop.id}`, () => getChannels(shops), { suspense: true });

	const isChain = useIsChain();

	const missingFields = [];
	stockItem.issues?.forEach((issue) => {
		if (issue === 'MissingProductTitle') missingFields.push('title');
		else if (issue === 'MissingProductDescription') missingFields.push('description');
		else if (issue === 'MissingProductBrand') missingFields.push('brand');
		else if (issue === 'MissingProductImages') missingFields.push('image');
		else if (issue === 'MissingProductCategory') missingFields.push('category');
		else if (issue === 'MissingDeliveryCategories') missingFields.push('menu categories');
	});

	return (
		<div>
			{missingFields.length > 0 && (
				<Card title="Fix suggestions">
					<p>We have identified some issues that require your attention.</p>
					<p className={styles.warning}>
						<StatusIcon status="error" /> Additional data required: {missingFields.join(', ')}.
					</p>
				</Card>
			)}
			{stockItem.productSource &&
				stockItem.productSource !== 'csv' &&
				stockItem.productSource !== 'myshop' &&
				stockItem.productSource !== 'brandbank' && (
					<Alert variant="info">
						<p>
							This product data is used for non-Google channels only. Google sources its own product information directly and
							doesn't use this data.
						</p>
					</Alert>
				)}
			<div className={styles.container}>
				<div className={styles.editCard}>
					<Stack>
						<Card
							data-cy="inventory-item-form"
							title={
								<>
									<span>Product data</span>
									<ProductSource stockItem={stockItem} />
								</>
							}
						>
							<EditForm key={`${shop.id}_${barcode}`} />
						</Card>
						<DeliveryChannelsCard stockItem={stockItem} shopId={shop.id} />
					</Stack>
				</div>

				<div className={styles.rightStack}>
					<Stack>
						{isChain ? <StockDataSummary stockItem={stockItem} /> : <StockData stockItem={stockItem} />}
						<ItemStatus stockItem={stockItem} shopId={shop.id} />
						<ChannelVisibilityCard shopId={shop.id} stockItem={stockItem} channels={channels} />
						<ProductCategory
							defaultCategory={stockItem.productCategory?.title}
							onCategorySelect={async (category) => {
								await customiseProduct(stockItem.barcode, shopId, { categoryIds: category ? [category.value] : [] });
							}}
						/>
					</Stack>
				</div>
			</div>
		</div>
	);
};

export default ProductDetails;
